import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useFingotiAuth } from '@fingoti/auth-js/react';
import {
	FingotiButton,
	FingotiLoading,
	FingotiHeader,
	FingotiMarkdown,
	FingotiTable,
	URLGenerator,
} from '@fingoti/components';

import { useTheme } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';

import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

import { ProfileData } from '../../context/ProfileData';
import { useProfileState } from '../../context/ProfileContext';
import { apiService } from '../../services/api.service';
import { useRoleCheck } from '../Utils/RoleCheck';

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
		marginTop: theme.spacing(6),
	},
	tableHead: {
		color: theme.palette.primary.main,
	},
	currentOrganisation: {
		background: theme.palette.primary.lighter,
	},
	actionIcons: {
		display: 'flex',
		alignItems: 'center',
	},
	actionIcon: {
		padding: 0,
		marginRight: theme.spacing(1),
		marginLeft: theme.spacing(1),
	},
	avatar: {
		fontSize: '1.1rem',
		border: '2px solid #E0E0E0',
		marginRight: theme.spacing(2),
	},
	organisationName: {
		padding: theme.spacing(1),
	},
	organisationWrapper: {
		display: 'flex',
		alignItems: 'center',
	},
	enabledIcon: {
		verticalAlign: 'middle',
		fill: theme.palette.success.main,
	},
	disabledIcon: {
		verticalAlign: 'middle',
		fill: theme.palette.error.main,
	},
	noItemsRow: {
		textAlign: 'center',
		height: theme.spacing(32),
		borderBottom: 'none',
	},
	noPerm: {
		textAlign: 'center',
		padding: theme.spacing(6),
	},
}));

export const OrganisationTenants = () => {
	const theme = useTheme();
	const classes = useStyles();
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const { enterOrgContext, isHomeOrg } = useFingotiAuth();
	const profileData = ProfileData();
	const { roleCheck } = useRoleCheck();
	const [thisLoading, setThisLoading] = useState(true);
	const { organisation, roles, loading } = useProfileState();
	const tableRef = useRef();

	useEffect(() => {
		setThisLoading(true);
		if (organisation && roles && !loading) {
			setThisLoading(false);
		}
	}, [organisation, roles, loading]);

	const getTenants = async (query) => {
		return apiService
			.getData(URLGenerator(query, '/organisation/tenants'))
			.then((data) => {
				data.tenants.sort((a, b) =>
					a.organisationName.localeCompare(b.organisationName)
				);
				return {
					data: data.tenants,
					page: data.pageNumber - 1,
					totalCount: data.count,
				};
			})
			.catch((error) => {
				enqueueSnackbar(error, { variant: 'error' });
			});
	};

	const switchOrganisation = async (targetId) => {
		try {
			// Trade the current token for one scoped to the tenant org. The server
			// resolves this to an act-as context (partner delegation). From here
			// getAccessToken() transparently returns the new token, so every API
			// call (and RoleCheck, via session.scopes) follows the new context.
			await enterOrgContext(targetId);
			// Refetch the profile so the rest of the app reflects the tenant.
			await profileData.getData();
			history.push('/organisation');
		} catch (error) {
			// A denied switch is not unrecoverable — the session is untouched.
			// FingotiOAuthError carries the human-readable reason on `description`.
			enqueueSnackbar(
				error?.description ?? error?.message ?? 'Unable to switch to tenant',
				{ variant: 'error' }
			);
		}
	};

	const organisationNameRenderer = (rd) => {
		return (
			<div className={classes.organisationWrapper}>
				<Avatar
					className={classes.avatar}
					size='small'
					src={`https://cdn.fingoti.com/images/avatars/${rd.id}`}>
					<AccountBalanceOutlinedIcon />
				</Avatar>
				{rd.organisationName}
			</div>
		);
	};

	const enabledRenderer = (rd) => {
		return rd.disabled ? (
			<BlockOutlinedIcon className={classes.disabledIcon} />
		) : (
			<CheckCircleOutlineIcon className={classes.enabledIcon} />
		);
	};

	const actionButtons = () => {
		return (
			<React.Fragment>
				<FingotiButton
					light
					color='primary'
					disabled={roleCheck('partner:write')}
					onClick={() => history.push('/register?tenant=true')}>
					new tenant
				</FingotiButton>
			</React.Fragment>
		);
	};

	const breadcrumbs = [
		{ text: 'Organisation', link: '/organisation' },
		{ text: 'Tenants', link: '' },
	];

	return (
		<React.Fragment>
			{thisLoading ? (
				<FingotiLoading />
			) : (
				<React.Fragment>
					<FingotiHeader
						breadcrumbs={breadcrumbs}
						actionButtons={actionButtons()}
						sectionIcon={AccountBalanceOutlinedIcon}
					/>
					<FingotiMarkdown path='/documentation/account/organisation/tenants.md' />
					{!roleCheck('partner:read') ? (
						<FingotiTable
							data={getTenants}
							tableRef={tableRef}
							columns={[
								{ title: 'id', field: 'id', hidden: true },
								{
									title: 'organisation name',
									field: 'organisationName',
									cellStyle: { padding: theme.spacing(1) },
									render: organisationNameRenderer,
								},
								{
									title: 'organisation number',
									field: 'organisationNumber',
								},
								{
									title: 'enabled',
									render: enabledRenderer,
								},
							]}
							actions={[
								(rowData) => ({
									icon: AutorenewIcon,
									tooltip: 'switch to tenant',
									// Nesting isn't allowed: exit the current org context
									// (banner in Layout) before switching again.
									disabled: rowData.disabled || !isHomeOrg,
									onClick: () => switchOrganisation(rowData.id),
								}),
							]}
						/>
					) : (
						<Typography className={classes.noPerm}>
							You do not have permission to view this page
						</Typography>
					)}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};
